import { useEffect, useRef } from 'react';
import { useParams } from '@tanstack/react-router';
import StoreHeader from 'app/javascript/components/store/StoreHeader';
import StoreCategoryNav from 'app/javascript/components/store/StoreCategoryNav';
import StoreCategorySection from 'app/javascript/components/store/StoreCategorySection';
import StoreEmptyState from 'app/javascript/components/store/StoreEmptyState';
import StoreLoading from 'app/javascript/components/store/StoreLoading';
import { useStoreCatalog } from 'app/javascript/queries/useStoreCatalog';
import { usePublicCompany } from 'app/javascript/queries/useCompanies';
import { trackEvent } from 'app/javascript/firebase';
import { ROUTES } from 'app/javascript/constants/routes';
import { storeProductsByCategory } from 'app/javascript/data/storeCatalogs';

export default function CompanyStorePage() {
  const { slug } = useParams({ from: ROUTES.COMPANY_STORE });

  // The catalog drives whether there is a store at all (a 404 → no store). The
  // public company fetch only supplies name/logo for the header (the WhatsApp
  // CTA lives on the product detail page now), so it stays separate: products
  // render once the catalog loads and the header degrades gracefully if the
  // company fetch fails. retry:false on both keeps a bad slug from hanging.
  const { data: catalog, isLoading: catalogLoading, isError: catalogError } = useStoreCatalog(slug);
  const { data: company, isLoading, isError } = usePublicCompany(slug, { retry: false });

  // Keyed to the slug (not a boolean) so navigating between two stores in the
  // same component instance re-fires the event for the new slug.
  const trackedSlugRef = useRef<string | null>(null);
  useEffect(() => {
    if (!catalog || trackedSlugRef.current === slug) return;
    trackedSlugRef.current = slug;
    trackEvent('store_viewed', { company_slug: slug, product_count: catalog.products.length });
  }, [catalog, slug]);

  if (catalogLoading) return <StoreLoading />;

  const sections = catalog ? storeProductsByCategory(catalog) : [];
  if (catalogError || !catalog || sections.length === 0) return <StoreEmptyState slug={slug} />;

  const academyName = company?.name ?? null;
  // The header still loads briefly even on error; it degrades to a neutral state
  // without an error screen.
  const loading = isLoading && !isError;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <StoreHeader academyName={academyName} logoUrl={company?.logo_url ?? null} loading={loading} />

      <StoreCategoryNav sections={sections} companySlug={slug} />

      <div className="mx-auto max-w-5xl space-y-16 px-4 py-12 sm:px-6 sm:py-16">
        {sections.map((section) => (
          <StoreCategorySection
            key={section.id}
            id={section.id}
            label={section.label}
            products={section.products}
            academyName={academyName ?? 'academia'}
            companySlug={slug}
          />
        ))}
      </div>
    </div>
  );
}
